import { type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

interface EmptyStateProps {
  /** Icon rendered in a soft circle at the top. Optional but recommended. */
  icon?: LucideIcon;
  /** Short headline ("No segments yet"). */
  title: string;
  /** Secondary line explaining what to do next. */
  description?: string;
  /** Optional CTA button(s). Typically a single "Create first X" action. */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Centered empty-state placeholder with icon + title + description + optional
 * CTA. Use this any time a list or page has no content to show yet. The
 * DataTable accepts this as its `emptyMessage` slot; it also works standalone
 * when a page's entire body is the empty state.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 py-12 text-center",
        className,
      )}
    >
      {Icon && (
        <div
          className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-elevated text-muted-foreground"
          aria-hidden="true"
        >
          <Icon size={20} strokeWidth={1.75} />
        </div>
      )}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description && (
          <p className="max-w-xs text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="mt-2 flex items-center gap-2">{action}</div>}
    </div>
  );
}
